/**
 * IRMA Backend API Client
 * 
 * This client communicates with the backend API server
 * which provides read-only access to on-chain data and
 * transaction builders for frontend signing.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3008"

export interface SubscriptionPlan {
  platformId: string
  planName: string
  priceInUsd: string // Price in cents (e.g., "9900" for $99)
  durationDays: number
  authority: string
}

export interface Subscription {
  subscriber: string
  planId: string
  planPrice: string
  startDate: string
  expiryDate: string
  isActive: boolean
  affiliateCredit: string
}

export interface AffiliateAccount {
  affiliate: string
  totalCredits: string
  totalWithdrawn: string
  availableBalance: string
}

export interface VaultInfo {
  authority: string
  totalCollected?: string
  totalWithdrawn?: string
}

export interface Withdrawal {
  id: string
  affiliate: string
  amount: string
  transactionSignature: string
  vaultAddress: string
  status: "pending" | "completed" | "failed"
  createdAt: string
  updatedAt: string
}

export interface WithdrawalStats {
  stats: {
    total: number
    completed: number
    pending: number
    failed: number
    totalAmount: string
  }
  topAffiliates: Array<{
    affiliate: string
    withdrawalCount: number
    totalAmount: string
  }>
}

export interface AdminConfig {
  authority: string
  adminWithdrawalBps: number
  adminWithdrawalTokenAccount: string
  marketPoolTokenAccount: string
  adminConfigPDA?: string
}

export interface AdminConfigBalances {
  adminWithdrawalBps: number
  adminWithdrawalPercentage: string
  balances: {
    adminWithdrawal: {
      account: string
      rawBalance: string
      balance: string
      balanceFormatted: string
    }
    marketPool: {
      account: string
      rawBalance: string
      balance: string
      balanceFormatted: string
    }
    total: {
      rawBalance: string
      balance: string
      balanceFormatted: string
    }
  }
}

export class IRMAAPIClient {
  baseUrl: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  // Health check
  async health(): Promise<{ status: string; programId: string }> {
    const response = await fetch(`${this.baseUrl}/health`)
    if (!response.ok) throw new Error("Health check failed")
    return response.json()
  }

  // Subscription Plan APIs
  async createPlan(
    platformId: string,
    planName: string,
    priceInUsd: number, // Price in USD (e.g., 99.00)
    durationDays: number
  ): Promise<{ success: boolean; transaction: string; plan: SubscriptionPlan }> {
    const response = await fetch(`${this.baseUrl}/api/plans`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        platformId,
        planName,
        priceInUsd,
        durationDays,
      }),
    })
    if (!response.ok) throw new Error("Failed to create plan")
    return response.json()
  }

  async getAllPlans(): Promise<SubscriptionPlan[]> {
    const response = await fetch(`${this.baseUrl}/api/plans`)
    if (!response.ok) {
      throw new Error("Failed to fetch plans")
    }
    return response.json()
  }

  async getPlan(platformId: string): Promise<SubscriptionPlan> {
    const response = await fetch(`${this.baseUrl}/api/plans/${platformId}`)
    if (!response.ok) {
      if (response.status === 404) throw new Error("Plan not found")
      throw new Error("Failed to fetch plan")
    }
    return response.json()
  }

  // Subscription APIs
  async getSubscription(subscriber: string, platformId: string): Promise<Subscription> {
    const response = await fetch(`${this.baseUrl}/api/subscriptions/${subscriber}/${platformId}`)
    if (!response.ok) {
      if (response.status === 404) throw new Error("Subscription not found")
      throw new Error("Failed to fetch subscription")
    }
    return response.json()
  }

  async validateSubscription(subscriber: string, platformId: string): Promise<boolean> {
    const response = await fetch(`${this.baseUrl}/api/subscriptions/${subscriber}/${platformId}/validate`)
    if (!response.ok) throw new Error("Validation failed")
    const data = await response.json()
    return data.isValid
  }

  async getAllSubscriptions(subscriber: string): Promise<any[]> {
    const response = await fetch(`${this.baseUrl}/api/subscriptions/${subscriber}`)
    console.log(response)
    if (!response.ok) throw new Error("Failed to fetch subscriptions")
    return response.json()
  }

  // Save subscription to database (call after successful on-chain subscription)
  async saveSubscription(
    subscriber: string,
    platformId: string,
    transaction?: string,
    autoRenewEnabled?: boolean
  ): Promise<{ success: boolean; subscription: Subscription }> {
    const response = await fetch(`${this.baseUrl}/api/subscriptions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        subscriber, 
        platformId, 
        transaction,
        autoRenewEnabled // Pass autoRenewEnabled if provided
      }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to save subscription" }))
      throw new Error(error.error || "Failed to save subscription")
    }
    return response.json()
  }

  // Legacy method signature for backward compatibility
  async saveSubscriptionOld(
    subscriber: string,
    platformId: string,
    transaction?: string
  ): Promise<{ success: boolean; subscription: Subscription }> {
    const response = await fetch(`${this.baseUrl}/api/subscriptions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscriber, platformId, transaction }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to save subscription" }))
      throw new Error(error.error || "Failed to save subscription")
    }
    return response.json()
  }

  // Affiliate APIs
  async getAffiliateAccount(affiliate: string): Promise<AffiliateAccount> {
    const response = await fetch(`${this.baseUrl}/api/affiliates/${affiliate}`)
    if (!response.ok) {
      if (response.status === 404) throw new Error("Affiliate account not found")
      throw new Error("Failed to fetch affiliate account")
    }
    return response.json()
  }

  // Vault APIs
  async getSubscriptionVault(): Promise<VaultInfo> {
    const response = await fetch(`${this.baseUrl}/api/vaults/subscription`)
    if (!response.ok) {
      if (response.status === 404) throw new Error("Vault not initialized")
      throw new Error("Failed to fetch vault")
    }
    return response.json()
  }

  async getWithdrawalVault(): Promise<VaultInfo> {
    const response = await fetch(`${this.baseUrl}/api/vaults/withdrawal`)
    if (!response.ok) {
      if (response.status === 404) throw new Error("Vault not initialized")
      throw new Error("Failed to fetch vault")
    }
    return response.json()
  }

  // Transaction Builders
  async buildSubscribeTransaction(
    subscriber: string,
    platformId: string,
    mint: string
  ): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/transactions/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscriber, platformId, mint }),
    })
    if (!response.ok) throw new Error("Failed to build transaction")
    return response.json()
  }

  async buildWithdrawTransaction(
    affiliate: string,
    amount: string,
    mint: string
  ): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/transactions/withdraw`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ affiliate, amount, mint }),
    })
    if (!response.ok) throw new Error("Failed to build transaction")
    return response.json()
  }

  // Auto-renewal APIs
  async enableAutoRenew(
    subscriber: string,
    platformId: string
  ): Promise<{ success: boolean; subscription: any }> {
    const response = await fetch(
      `${this.baseUrl}/api/subscriptions/${subscriber}/${platformId}/enable-auto-renew`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }
    )
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to enable auto-renewal" }))
      throw new Error(error.error || "Failed to enable auto-renewal")
    }
    return response.json()
  }

  async disableAutoRenew(
    subscriber: string,
    platformId: string
  ): Promise<{ success: boolean; subscription: any }> {
    const response = await fetch(
      `${this.baseUrl}/api/subscriptions/${subscriber}/${platformId}/disable-auto-renew`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }
    )
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to disable auto-renewal" }))
      throw new Error(error.error || "Failed to disable auto-renewal")
    }
    return response.json()
  }

  async syncSubscription(
    subscriber: string,
    platformId: string
  ): Promise<{ success: boolean; subscription: any }> {
    const response = await fetch(
      `${this.baseUrl}/api/subscriptions/${subscriber}/${platformId}/sync`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }
    )
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to sync subscription" }))
      throw new Error(error.error || "Failed to sync subscription")
    }
    return response.json()
  }

  // Withdrawal APIs
  async saveWithdrawal(
    affiliate: string,
    amount: string,
    transactionSignature: string,
    vaultAddress: string,
    status: "pending" | "completed" | "failed" = "completed"
  ): Promise<{ success: boolean; withdrawal: Withdrawal }> {
    const response = await fetch(`${this.baseUrl}/api/withdrawals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        affiliate,
        amount,
        transactionSignature,
        vaultAddress,
        status,
      }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to save withdrawal" }))
      throw new Error(error.error || "Failed to save withdrawal")
    }
    return response.json()
  }

  async getWithdrawals(params?: {
    affiliate?: string
    status?: "pending" | "completed" | "failed"
    page?: number
    limit?: number
    sortBy?: string
    sortOrder?: "asc" | "desc"
  }): Promise<{
    withdrawals: Withdrawal[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    const queryParams = new URLSearchParams()
    if (params?.affiliate) queryParams.append("affiliate", params.affiliate)
    if (params?.status) queryParams.append("status", params.status)
    if (params?.page) queryParams.append("page", params.page.toString())
    if (params?.limit) queryParams.append("limit", params.limit.toString())
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy)
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder)

    const url = `${this.baseUrl}/api/withdrawals${queryParams.toString() ? `?${queryParams.toString()}` : ""}`
    const response = await fetch(url)
    if (!response.ok) throw new Error("Failed to fetch withdrawals")
    return response.json()
  }

  async getWithdrawalsByAffiliate(
    affiliate: string,
    params?: {
      page?: number
      limit?: number
      sortBy?: string
      sortOrder?: "asc" | "desc"
    }
  ): Promise<{
    withdrawals: Withdrawal[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append("page", params.page.toString())
    if (params?.limit) queryParams.append("limit", params.limit.toString())
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy)
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder)

    const url = `${this.baseUrl}/api/withdrawals/affiliate/${affiliate}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`
    const response = await fetch(url)
    if (!response.ok) throw new Error("Failed to fetch affiliate withdrawals")
    return response.json()
  }

  async getWithdrawalByTransaction(signature: string): Promise<Withdrawal> {
    const response = await fetch(`${this.baseUrl}/api/withdrawals/transaction/${signature}`)
    if (!response.ok) {
      if (response.status === 404) throw new Error("Withdrawal not found")
      throw new Error("Failed to fetch withdrawal")
    }
    return response.json()
  }

  async getWithdrawalStats(params?: {
    startDate?: string
    endDate?: string
  }): Promise<WithdrawalStats> {
    const queryParams = new URLSearchParams()
    if (params?.startDate) queryParams.append("startDate", params.startDate)
    if (params?.endDate) queryParams.append("endDate", params.endDate)

    const url = `${this.baseUrl}/api/withdrawals/stats${queryParams.toString() ? `?${queryParams.toString()}` : ""}`
    const response = await fetch(url)
    if (!response.ok) throw new Error("Failed to fetch withdrawal stats")
    return response.json()
  }

  // Price Oracle APIs
  async getIrmaPrice(): Promise<{
    price: number
    priceScaled: string
    lastUpdated: string
  }> {
    const response = await fetch(`${this.baseUrl}/api/price/irma`)
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to fetch IRMA price" }))
      throw new Error(error.error || "Failed to fetch IRMA price")
    }
    return response.json()
  }

  /** Update oracle price (backend signs with server wallet). Use for Devnet testing only. */
  async updateOraclePrice(price: number): Promise<{
    success: boolean
    price: number
    priceScaled: number
    transaction: string
  }> {
    const response = await fetch(`${this.baseUrl}/api/price/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ price }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to update price" }))
      throw new Error(error.error || "Failed to update price")
    }
    return response.json()
  }

  async calculateIrmaFromUsd(usdAmount: number): Promise<{
    usdAmount: number
    irmaPrice: number
    irmaAmount: number
    irmaAmountWithBonus: number
    credits: number
    creditsWithBonus: number
    bonus: string
  }> {
    const response = await fetch(`${this.baseUrl}/api/price/calculate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ usdAmount }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to calculate IRMA amount" }))
      throw new Error(error.error || "Failed to calculate IRMA amount")
    }
    return response.json()
  }

  // Staking APIs
  async getStakingInfo(staker: string): Promise<{
    staker: string
    stakes: any[]
    totalStaked: string
    totalRewards: string
  }> {
    const response = await fetch(`${this.baseUrl}/api/staking/${staker}`)
    if (!response.ok) throw new Error("Failed to fetch staking info")
    return response.json()
  }

  async getStakingConfig(): Promise<{
    bronzeApy: number
    silverApy: number
    goldApy: number
    platinumApy: number
    bronzePenalty: number
    silverPenalty: number
    goldPenalty: number
    platinumPenalty: number
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/staking/config`)
      if (!response.ok) {
        // If config doesn't exist, return default values
        if (response.status === 404) {
          return {
            bronzeApy: 500,
            silverApy: 1000,
            goldApy: 1500,
            platinumApy: 2500,
            bronzePenalty: 5000,
            silverPenalty: 4000,
            goldPenalty: 3000,
            platinumPenalty: 2000,
          }
        }
        const errorData = await response.json().catch(() => ({ error: "Failed to fetch staking config" }))
        throw new Error(errorData.error || "Failed to fetch staking config")
      }
      const data = await response.json()
      // Convert string values to numbers
      return {
        bronzeApy: typeof data.bronzeApy === 'string' ? parseInt(data.bronzeApy) : data.bronzeApy || 500,
        silverApy: typeof data.silverApy === 'string' ? parseInt(data.silverApy) : data.silverApy || 1000,
        goldApy: typeof data.goldApy === 'string' ? parseInt(data.goldApy) : data.goldApy || 1500,
        platinumApy: typeof data.platinumApy === 'string' ? parseInt(data.platinumApy) : data.platinumApy || 2500,
        bronzePenalty: typeof data.bronzePenalty === 'string' ? parseInt(data.bronzePenalty) : data.bronzePenalty || 5000,
        silverPenalty: typeof data.silverPenalty === 'string' ? parseInt(data.silverPenalty) : data.silverPenalty || 4000,
        goldPenalty: typeof data.goldPenalty === 'string' ? parseInt(data.goldPenalty) : data.goldPenalty || 3000,
        platinumPenalty: typeof data.platinumPenalty === 'string' ? parseInt(data.platinumPenalty) : data.platinumPenalty || 2000,
      }
    } catch (error: any) {
      console.error("Error fetching staking config:", error)
      // Return default values on error
      return {
        bronzeApy: 500,
        silverApy: 1000,
        goldApy: 1500,
        platinumApy: 2500,
        bronzePenalty: 5000,
        silverPenalty: 4000,
        goldPenalty: 3000,
        platinumPenalty: 2000,
      }
    }
  }

  async initializeStakingVault(): Promise<{
    success: boolean
    message: string
    transaction?: string
    stakingVault?: string
    error?: string
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/staking/initialize-vault`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
      const data = await response.json()
      if (!response.ok) {
        // If already initialized, that's fine
        if (data.error?.includes("already initialized")) {
          return { success: true, message: "Vault already initialized", stakingVault: data.stakingVault }
        }
        throw new Error(data.error || "Failed to initialize staking vault")
      }
      return data
    } catch (error: any) {
      return { success: false, error: error.message || "Failed to initialize staking vault" }
    }
  }

  async initializeStakingConfig(): Promise<{
    success: boolean
    message: string
    transaction?: string
    stakingConfig?: string
    error?: string
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/staking/initialize-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
      const data = await response.json()
      if (!response.ok) {
        // If already initialized, that's fine
        if (data.error?.includes("already initialized")) {
          return { success: true, message: "Config already initialized", stakingConfig: data.stakingConfig }
        }
        throw new Error(data.error || "Failed to initialize staking config")
      }
      return data
    } catch (error: any) {
      return { success: false, error: error.message || "Failed to initialize staking config" }
    }
  }

  async buildStakeTransaction(
    staker: string,
    amount: number,
    tier: number
  ): Promise<{
    instruction: string
    accounts: any
  }> {
    const response = await fetch(`${this.baseUrl}/api/staking/stake`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ staker, amount, tier }),
    })
    if (!response.ok) throw new Error("Failed to build stake transaction")
    return response.json()
  }

  async buildUnstakeTransaction(
    staker: string,
    tier: number,
    early: boolean
  ): Promise<{
    instruction: string
    accounts: any
  }> {
    const response = await fetch(`${this.baseUrl}/api/staking/unstake`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ staker, tier, early }),
    })
    if (!response.ok) throw new Error("Failed to build unstake transaction")
    return response.json()
  }

  async buildClaimRewardsTransaction(
    staker: string,
    tier: number
  ): Promise<{
    instruction: string
    accounts: any
  }> {
    const response = await fetch(`${this.baseUrl}/api/staking/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ staker, tier }),
    })
    if (!response.ok) throw new Error("Failed to build claim transaction")
    return response.json()
  }

  // Admin Config APIs
  async getAdminConfig(): Promise<AdminConfig> {
    const response = await fetch(`${this.baseUrl}/api/admin/config`)
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Admin config not initialized")
      }
      throw new Error("Failed to fetch admin config")
    }
    return response.json()
  }

  async initializeAdminConfig(
    adminWithdrawalBps: number,
    adminWithdrawalTokenAccount: string,
    irmaMint?: string
  ): Promise<{
    success: boolean
    message: string
    adminWithdrawalBps: number
    adminWithdrawalPercentage: string
    adminWithdrawalTokenAccount: string
    marketPoolTokenAccount: string
    transaction: string
    adminConfigPDA: string
  }> {
    const response = await fetch(`${this.baseUrl}/api/admin/config/initialize`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        adminWithdrawalBps,
        adminWithdrawalTokenAccount,
        irmaMint,
      }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to initialize admin config" }))
      throw new Error(error.error || "Failed to initialize admin config")
    }
    return response.json()
  }

  async updateAdminConfig(
    adminWithdrawalBps?: number,
    adminWithdrawalTokenAccount?: string,
    marketPoolTokenAccount?: string
  ): Promise<{
    success: boolean
    message: string
    transaction: string
    updatedFields: {
      adminWithdrawalBps?: number
      adminWithdrawalPercentage?: string
      adminWithdrawalTokenAccount?: string
      marketPoolTokenAccount?: string
    }
  }> {
    const response = await fetch(`${this.baseUrl}/api/admin/config/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        adminWithdrawalBps,
        adminWithdrawalTokenAccount,
        marketPoolTokenAccount,
      }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to update admin config" }))
      throw new Error(error.error || "Failed to update admin config")
    }
    return response.json()
  }

  async getAdminConfigBalances(): Promise<AdminConfigBalances> {
    const response = await fetch(`${this.baseUrl}/api/admin/config/balances`)
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Admin config not initialized")
      }
      throw new Error("Failed to fetch admin config balances")
    }
    return response.json()
  }

  // MoonPay Conversion APIs
  async getMoonPayConversions(limit: number = 50): Promise<{
    success: boolean
    conversions: any[]
    count: number
  }> {
    const response = await fetch(`${this.baseUrl}/api/moonpay/conversions?limit=${limit}`)
    if (!response.ok) {
      throw new Error("Failed to fetch MoonPay conversions")
    }
    return response.json()
  }

  async getPendingMoonPayConversions(): Promise<{
    success: boolean
    conversions: any[]
    count: number
  }> {
    const response = await fetch(`${this.baseUrl}/api/moonpay/conversions/pending`)
    if (!response.ok) {
      throw new Error("Failed to fetch pending MoonPay conversions")
    }
    return response.json()
  }

  async getMoonPayConversion(id: string): Promise<{
    success: boolean
    conversion: any
  }> {
    const response = await fetch(`${this.baseUrl}/api/moonpay/conversions/${id}`)
    if (!response.ok) {
      throw new Error("Failed to fetch MoonPay conversion")
    }
    return response.json()
  }
}

// Export singleton instance
export const apiClient = new IRMAAPIClient()
